import { FC, ReactNode } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { useSnackbar, VariantType } from "notistack";
import { useNavigate } from "react-router-dom";
import LoggingAPI from "@/api/logging";
import { createEvidence, getIpAddress } from "@/api";
import EvidenceCocAPI from "@/api/evidence/coc";
import moment from "moment";

import { FormWrapper } from "@/components/Forms/components/MonolithFormComponents";
import DefaultEvidenceForm from "./DefaultEvidenceForm";
import { db_timestamp } from "@/utils/date-format";
import { processCustomAttributesForAPI } from "./utils/customAttributeUtils";
import {
  EvidenceFormData,
  EditEvidenceFormData,
  CreateEvidenceOptions,
} from "../evidence.types";
import { SignatureFieldRef } from "@/Features";
import { checkIntakeFieldsDirty } from "@/Features/ChainOfCustody/Forms/IntakeCocForm";

type CreateEvidenceFormProps = {
  width?: number | undefined;
  onSubmit: () => void;
  onCancel: () => void;
  submitText?: string;
  cancelText?: string;
  description?: ReactNode;
  formTitle?: ReactNode;
  includeCaseField?: boolean;
  navigateToItem?: boolean;
  defaultFormData?: EvidenceFormData;
  createOptions?: CreateEvidenceOptions;
};

const CreateEvidenceForm: FC<CreateEvidenceFormProps> = ({
  width,
  onSubmit,
  onCancel,
  submitText,
  cancelText,
  description,
  formTitle,
  includeCaseField = false,
  navigateToItem = false,
  defaultFormData,
  createOptions,
}) => {
  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // A child create is defined by the parent linkage passed to createEvidence;
  // labels, the intake form and the CoC gate all derive from it so the UI and
  // the persisted is_parent cannot disagree.
  const isChildEvidence = createOptions?.is_parent === 0;

  const handleSubmit = async (
    data: EvidenceFormData,
    diffData?: EditEvidenceFormData,
    signatures?: {
      receivedBySig: SignatureFieldRef;
      receivedFromSig: SignatureFieldRef;
    }
  ) => {
    try {
      const formattedData: Record<string, unknown> = {
        ...data,
        contact_id: data.contact_id || null,
      };

      // Remove fields that shouldn't be sent to API
      delete formattedData.contact;

      // Remove CoC fields from evidence creation - they'll be handled separately
      const cocData = {
        custody_to: data.custody_to,
        custody_from: data.custody_from,
        coc_intake_timestamp: data.coc_intake_timestamp,
        location_received: data.location_received,
        reason: data.reason,
      };

      // Remove CoC fields from evidence data
      delete formattedData.custody_to;
      delete formattedData.custody_from;
      delete formattedData.coc_intake_timestamp;
      delete formattedData.location_received;
      delete formattedData.reason;

      const customAttributes = processCustomAttributesForAPI(formattedData)
        // Only include custom attributes that have a value (non-null, non-undefined, non-empty string)
        .filter(({ value }) =>
          Array.isArray(value)
            ? value.length > 0
            : value !== null && value !== undefined && value !== ""
        );

      if (customAttributes.length > 0) {
        formattedData.custom_attributes = customAttributes;
      }

      const result = await createEvidence(formattedData, createOptions);

      // Handle Chain of Custody data separately if the user entered intake
      // details. For child items, the prepopulated custody_from / reason only
      // count once edited away from their defaults — an untouched prefill
      // leaves the new item with an empty chain of custody.
      const prepopulatedIntake = isChildEvidence
        ? {
            custody_from: defaultFormData?.custody_from,
            reason: defaultFormData?.reason,
          }
        : undefined;
      if (checkIntakeFieldsDirty(cocData, prepopulatedIntake)) {
        const ipAddress = await getIpAddress();
        const signature: Record<string, unknown> = {};

        signature.custody_to_sig =
          signatures?.receivedBySig && !signatures.receivedBySig.isEmpty?.()
            ? {
                signature: signatures.receivedBySig.getSignature?.(),
                signer: (cocData.custody_to as any)?.location_name,
                timestamp: moment().toISOString(),
                userAgent: window.navigator.userAgent,
                ip_address: ipAddress,
                monolith_user: {
                  first_name: currentUser.first_name,
                  last_name: currentUser.last_name,
                  full_name: currentUser.full_name,
                  email: currentUser.email,
                  title: currentUser.title,
                  user_id: currentUser.user_id,
                },
                esig: signatures.receivedBySig.isESignature,
              }
            : null;

        signature.custody_from_sig =
          signatures?.receivedFromSig && !signatures.receivedFromSig.isEmpty?.()
            ? {
                signature: signatures.receivedFromSig.getSignature?.(),
                signer: cocData.custody_from,
                timestamp: moment().toISOString(),
                userAgent: window.navigator.userAgent,
                ip_address: ipAddress,
                monolith_user: {
                  first_name: currentUser.first_name,
                  last_name: currentUser.last_name,
                  full_name: currentUser.full_name,
                  email: currentUser.email,
                  title: currentUser.title,
                  user_id: currentUser.user_id,
                },
              }
            : null;

        // Create intake details using the same API as IntakeCocModal
        const intakeData = {
          evidence_id: result.evidence_id,
          custody_to: (cocData.custody_to as any)?.location_id || 0,
          custody_from: cocData.custody_from,
          coc_intake_timestamp: cocData.coc_intake_timestamp
            ? db_timestamp(cocData.coc_intake_timestamp, true)
            : db_timestamp(new Date().toISOString(), true),
          location_received: cocData.location_received,
          reason: cocData.reason || "Evidence Intake",
          coc_type: "Intake",
          signature: JSON.stringify(signature),
          entered_by: currentUser.full_name,
        };

        await EvidenceCocAPI.createIntakeDetails(intakeData);
      }

      onSubmit?.();
      const itemLabel = isChildEvidence
        ? "Child Evidence Item"
        : "Evidence Item";
      enqueueSnackbar(`Created ${itemLabel} ${result.evidence_number}`, {
        variant: "success" as VariantType,
      });

      LoggingAPI.createLog({
        user_id: currentUser.user_id,
        message: `Created ${itemLabel}: ${result.evidence_number}`,
        event: "evidence:create",
        object_type: "evidence",
      });

      if (navigateToItem) {
        navigate(`/evidence/${result.uuid}/overview`, {
          state: { from: "case:evidence" },
        });
      }
    } catch (error) {
      enqueueSnackbar(
        "An error occurred while creating this evidence item. Please try again.",
        { variant: "error" as VariantType }
      );
    }
  };

  return (
    <FormWrapper width={width}>
      <DefaultEvidenceForm
        formTitle={formTitle ?? submitText ?? "Create Evidence"}
        description={description}
        isChildEvidence={isChildEvidence}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        defaultFormData={defaultFormData}
        buttonProps={{
          submitText: submitText || "Create Evidence",
          cancelText: cancelText || "Cancel",
        }}
        includeCaseField={includeCaseField}
      />
    </FormWrapper>
  );
};

export default CreateEvidenceForm;
